/* ─────────────────────────────────────────
   BannerParticles.ts
   HTML5 Canvas floating particle bubbles
───────────────────────────────────────── */

interface Particle {
  x: number;
  y: number;
  r: number;
  dx: number;
  dy: number;
  o: number;
}

const PARTICLE_COUNT = 32;

function rand(min: number, max: number): number {
  return Math.random() * (max - min) + min;
}

function buildParticles(w: number, h: number): Particle[] {
  const particles: Particle[] = [];
  for (let i = 0; i < PARTICLE_COUNT; i++) {
    particles.push({
      x: rand(0, w),
      y: rand(0, h),
      r: rand(0.5, 2.7),
      dx: rand(-0.35, 0.35),
      dy: rand(-0.25, 0.25),
      o: rand(0.08, 0.43),
    });
  }
  return particles;
}

export function mountBannerParticles(bannerEl: HTMLElement): () => void {
  /* ── Canvas ── */
  const canvas = document.createElement('canvas');
  canvas.id = 'particleCanvas';
  canvas.style.cssText =
    'position:absolute;top:0;left:0;width:100%;height:100%;pointer-events:none;z-index:1;';
  bannerEl.appendChild(canvas);

  canvas.width = canvas.offsetWidth;
  canvas.height = canvas.offsetHeight;

  const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;

  let particles: Particle[] = buildParticles(canvas.width, canvas.height);
  let animationId: number = 0;

  /* ── Draw loop ── */
  function drawParticles(): void {
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    for (const p of particles) {
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(255,255,255,${p.o})`;
      ctx.fill();

      p.x += p.dx;
      p.y += p.dy;

      if (p.x < 0 || p.x > canvas.width)  p.dx *= -1;
      if (p.y < 0 || p.y > canvas.height) p.dy *= -1;
    }

    animationId = requestAnimationFrame(drawParticles);
  }

  drawParticles();

  /* ── Resize ── */
  const onResize = (): void => {
    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;
    particles = buildParticles(canvas.width, canvas.height);
  };
  window.addEventListener('resize', onResize);

  /* ── Accent line ── */
  const accentLine = document.createElement('div');
  accentLine.style.cssText = [
    'position:absolute',
    'bottom:0',
    'left:0',
    'right:0',
    'height:3px',
    'background:linear-gradient(90deg,#E8C97A,#4DB8C4,#2D5A4E)',
    'z-index:2',
    'width:0',
    'transition:width 0.8s ease 0.6s',
  ].join(';');
  bannerEl.appendChild(accentLine);

  /* Trigger width animation on next frame so transition fires */
  requestAnimationFrame((): void => {
    requestAnimationFrame((): void => {
      accentLine.style.width = '100%';
    });
  });

  /* ── Cleanup ── */
  return (): void => {
    cancelAnimationFrame(animationId);
    window.removeEventListener('resize', onResize);
    canvas.remove();
    accentLine.remove();
  };
}
